<template>
  <v-dialog :model-value="modelValue" max-width="1200" scrollable persistent @update:model-value="close">
    <v-card v-if="draft">
      <v-card-title class="d-flex align-center flex-wrap" style="gap: 8px;">
        <v-icon class="mr-1">{{ draft.icon || 'mdi-robot-outline' }}</v-icon>
        <span>{{ isNew ? t('admConfig.aiActions.newTitle') : t('admConfig.aiActions.editTitle') }}</span>
        <code v-if="draft.id" class="text-body-2">{{ draft.id }}</code>
        <v-chip v-if="draft.steps?.length" size="small" label variant="tonal">
          {{ t('admConfig.aiActions.stepCount', { count: draft.steps.length }) }}
        </v-chip>
      </v-card-title>

      <v-card-text style="max-height: 72vh;">
        <v-alert v-if="errors.length" type="error" variant="tonal" density="compact" class="mb-3">
          <div v-for="(err, i) in errors" :key="i">{{ err }}</div>
        </v-alert>

        <!-- Un'azione spenta qui è spenta in ogni stato che la richiama -->
        <div class="d-flex align-center flex-wrap mb-3" style="gap: 12px;">
          <v-switch
            v-model="draft.enabled"
            :label="draft.enabled === false
              ? t('admConfig.statusEditor.verifications.disabled')
              : t('admConfig.statusEditor.verifications.enabled')"
            color="primary"
            density="compact"
            hide-details
            class="flex-grow-0"
          />
          <span class="text-caption text-medium-emphasis">
            {{ t('admConfig.aiActions.enabledHint') }}
          </span>
        </div>

        <v-row dense>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="draft.id"
              :label="t('admConfig.statusEditor.verifications.id')"
              :hint="t('admConfig.aiActions.idHint')"
              :disabled="!isNew"
              density="compact"
              persistent-hint
            />
          </v-col>
          <v-col cols="12" md="5">
            <v-text-field
              v-model="draft.label"
              :label="t('admConfig.statusEditor.verifications.label')"
              density="compact"
              counter="80"
              hide-details
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="draft.icon"
              :label="t('admConfig.statusEditor.verifications.icon')"
              placeholder="mdi-shield-check-outline"
              density="compact"
              hide-details
            >
              <template v-slot:prepend-inner>
                <v-icon size="small">{{ draft.icon || 'mdi-help' }}</v-icon>
              </template>
            </v-text-field>
          </v-col>

          <v-col v-if="!isTool" cols="12" md="5">
            <v-combobox
              v-model="draft.skill"
              :items="skillItems"
              item-title="title"
              item-value="value"
              :return-object="false"
              :label="t('admConfig.statusEditor.verifications.skill')"
              :hint="t('admConfig.statusEditor.verifications.skillHint')"
              density="compact"
              persistent-hint
            />
            <v-chip
              v-if="skillState(draft) === 'new'"
              size="x-small" label color="warning" variant="tonal" class="mt-1"
            >
              <v-icon start size="x-small">mdi-alert-outline</v-icon>
              {{ t('admConfig.statusEditor.verifications.skillNew') }}
            </v-chip>
          </v-col>
          <v-col v-if="!isTool" cols="12" md="4">
            <v-combobox
              v-model="draft.validationField"
              :items="checkboxFieldItems"
              item-title="title"
              item-value="value"
              :return-object="false"
              :label="t('admConfig.statusEditor.verifications.validationField')"
              :hint="t('admConfig.aiActions.validationFieldHint')"
              density="compact"
              persistent-hint
              clearable
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="draft.engine"
              :items="engineItems"
              item-title="title"
              item-value="value"
              :label="t('admConfig.statusEditor.verifications.engine')"
              density="compact"
              hide-details
            />
          </v-col>

          <!-- Motore tool: l'azione punta a tool + chiamata; input, output e regola di esito stanno
               nel pannello del tool (Classes/tools.json) -->
          <template v-if="isTool">
            <v-col cols="12" md="4">
              <v-select
                v-model="draft.tool"
                :items="toolItems"
                item-title="title"
                item-value="value"
                :label="t('admConfig.aiActions.tool')"
                density="compact"
                hide-details
                @update:model-value="draft.call = ''"
              />
            </v-col>
            <v-col cols="12" md="5">
              <v-select
                v-model="draft.call"
                :items="callItems"
                item-title="title"
                item-value="value"
                :label="t('admConfig.aiActions.call')"
                :hint="t('admConfig.aiActions.toolHint')"
                :disabled="!draft.tool"
                density="compact"
                persistent-hint
              />
            </v-col>
          </template>

          <v-col cols="12" md="2">
            <v-text-field
              :model-value="draft.maxRuns"
              :label="t('admConfig.statusEditor.verifications.maxRuns')"
              :hint="t('admConfig.statusEditor.verifications.maxRunsHint')"
              type="number"
              min="0"
              :max="MAX_RUNS_CEILING"
              density="compact"
              persistent-hint
              @update:model-value="value => draft.maxRuns = value === '' ? 0 : Number(value)"
            />
          </v-col>
          <v-col v-if="!isTool" cols="12" md="2" class="d-flex align-center">
            <v-switch
              v-model="draft.analyzeFiles"
              :label="t('admConfig.statusEditor.verifications.analyzeFiles')"
              color="primary"
              density="compact"
              hide-details
            />
          </v-col>
          <v-col v-if="!isTool" cols="12" md="4">
            <v-combobox
              v-model="draft.folders"
              :label="t('admConfig.statusEditor.verifications.folders')"
              :hint="t('admConfig.statusEditor.verifications.foldersHint')"
              density="compact"
              persistent-hint
              multiple
              chips
              closable-chips
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="draft.roles"
              :items="roleItems"
              item-title="title"
              item-value="value"
              :label="t('admConfig.aiActions.roles')"
              :hint="draft.roles.length
                ? t('admConfig.aiActions.rolesHint')
                : t('admConfig.aiActions.rolesHintAll')"
              density="compact"
              persistent-hint
              multiple
              chips
              closable-chips
              :disabled="!roleItems.length"
              :messages="roleItems.length ? undefined : t('admConfig.statusEditor.status.rolesEmpty')"
            />
          </v-col>
          <v-col v-if="!isTool" cols="12" md="4">
            <v-combobox
              v-model="draft.model"
              :items="modelItems"
              :label="t('admConfig.statusEditor.verifications.model')"
              :hint="modelHint(draft.model)"
              :placeholder="defaultModel || undefined"
              density="compact"
              persistent-hint
              clearable
            />
          </v-col>
        </v-row>

        <!-- Documento prodotto: cartella e nome SOLO per questa azione. Vuoto = come nel pannello
             del tool. È così che due azioni sulla stessa chiamata danno due documenti diversi
             (es. una CI prévia e la CI definitiva, emesse in due fasi). -->
        <template v-if="isTool && callFileSpec">
          <v-divider class="my-3" />
          <div class="text-subtitle-2 mb-1">{{ t('admConfig.tools.file') }}</div>
          <div class="text-caption text-medium-emphasis mb-2">
            {{ t('admConfig.aiActions.fileOverrideHint') }}
          </div>
          <v-row dense>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="draft.file.folder"
                :label="t('admConfig.tools.fileFolder')"
                :placeholder="toolFilePlaceholder('folder')"
                :hint="t('admConfig.tools.fileFolderHint')"
                density="compact"
                persistent-hint
                clearable
              />
            </v-col>
            <v-col cols="12" md="8">
              <v-text-field
                v-model="draft.file.nameTemplate"
                :label="t('admConfig.tools.fileName')"
                :placeholder="toolFilePlaceholder('nameTemplate')"
                :hint="t('admConfig.tools.fileNameHint', {
                  placeholders: callFileSpec.placeholders.map(ph => `{${ph}}`).join(' ') })"
                density="compact"
                persistent-hint
                clearable
              />
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="draft.file.footer"
                :label="t('admConfig.tools.fileFooter')"
                :placeholder="toolFilePlaceholder('footer')"
                :hint="t('admConfig.tools.fileFooterHint')"
                rows="2"
                density="compact"
                persistent-hint
                clearable
              />
            </v-col>
          </v-row>
        </template>

        <v-divider class="my-3" />

        <!-- Condizioni di attivazione: valgono per OGNI stato che richiama questa azione, a meno che
             lo stato non ne dichiari di proprie (Editor degli stati -> Azioni IA). -->
        <ConditionsEditor
          v-model="draft.conditionalOn"
          :fields="fields"
          :label="t('admConfig.conditions.title')"
          :hint="t('admConfig.conditions.actionHint')"
        />

        <!-- Procedura a più step: un solo pulsante che esegue gli step in sequenza e riprende da
             dove si era fermato. Senza step, l'azione è un giro unico. -->
        <template v-if="!isTool">
        <v-divider class="my-3" />
        <div class="d-flex align-center flex-wrap mb-2" style="gap: 8px;">
          <span class="text-caption text-medium-emphasis">
            {{ t('admConfig.statusEditor.verifications.steps') }}
          </span>
          <v-chip v-if="draft.steps?.length" size="x-small" label>{{ draft.steps.length }}</v-chip>
          <v-btn size="x-small" variant="text" color="primary" prepend-icon="mdi-plus" @click="addStep">
            {{ t('admConfig.statusEditor.verifications.addStep') }}
          </v-btn>
          <span class="text-caption text-medium-emphasis">
            {{ t('admConfig.statusEditor.verifications.stepsHint') }}
          </span>
        </div>

        <v-row
          v-for="(step, stepIndex) in (draft.steps || [])"
          :key="stepIndex"
          dense
          class="align-center step-row"
        >
          <v-col cols="12" md="1" class="text-center text-caption text-medium-emphasis">
            {{ stepIndex + 1 }}
          </v-col>
          <v-col cols="12" md="2">
            <v-text-field
              v-model="step.id"
              :label="t('admConfig.statusEditor.verifications.stepId')"
              density="compact"
              hide-details
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-text-field
              v-model="step.label"
              :label="t('admConfig.statusEditor.verifications.stepLabel')"
              density="compact"
              hide-details
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-combobox
              v-model="step.skill"
              :items="skillItems"
              item-title="title"
              item-value="value"
              :return-object="false"
              :label="t('admConfig.statusEditor.verifications.skill')"
              density="compact"
              hide-details
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-combobox
              v-model="step.validationField"
              :items="checkboxFieldItems"
              item-title="title"
              item-value="value"
              :return-object="false"
              :label="t('admConfig.statusEditor.verifications.stepValidationField')"
              density="compact"
              hide-details
              clearable
            />
          </v-col>
          <v-col cols="12" md="1">
            <v-text-field
              :model-value="step.maxRuns"
              :label="t('admConfig.statusEditor.verifications.maxRuns')"
              type="number"
              min="0"
              :max="MAX_RUNS_CEILING"
              density="compact"
              hide-details
              @update:model-value="value => step.maxRuns = value === '' ? 0 : Number(value)"
            />
          </v-col>
          <v-col cols="12" md="1" class="d-flex align-center justify-end">
            <v-btn icon variant="text" size="x-small" :disabled="stepIndex === 0" @click="moveStep(stepIndex, -1)">
              <v-icon size="small">mdi-arrow-up</v-icon>
            </v-btn>
            <v-btn
              icon variant="text" size="x-small"
              :disabled="stepIndex === draft.steps.length - 1"
              @click="moveStep(stepIndex, 1)"
            >
              <v-icon size="small">mdi-arrow-down</v-icon>
            </v-btn>
            <v-btn icon variant="text" size="x-small" color="error" @click="removeStep(stepIndex)">
              <v-icon size="small">mdi-delete</v-icon>
            </v-btn>
          </v-col>
          <v-col cols="12" md="8" class="pt-0">
            <v-combobox
              v-model="step.folders"
              :label="t('admConfig.statusEditor.verifications.stepFolders')"
              density="compact"
              hide-details
              multiple
              chips
              closable-chips
            />
          </v-col>
          <v-col cols="12" md="4" class="pt-0">
            <v-combobox
              v-model="step.model"
              :items="modelItems"
              :label="t('admConfig.statusEditor.verifications.stepModel')"
              :placeholder="draft.model || defaultModel || undefined"
              density="compact"
              hide-details
              clearable
            />
          </v-col>
        </v-row>
        </template>
      </v-card-text>

      <v-divider />
      <v-card-actions>
        <span v-if="usedBy.length" class="text-caption text-medium-emphasis ml-2">
          {{ t('admConfig.aiActions.usedByHint', { statuses: usedBy.join(', ') }) }}
        </span>
        <v-spacer />
        <v-btn variant="text" @click="close(false)">{{ t('admConfig.statusEditor.cancel') }}</v-btn>
        <v-btn variant="flat" color="primary" @click="commit">{{ t('admConfig.statusEditor.confirm') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import ConditionsEditor from './ConditionsEditor.vue'
import { apiCall } from '../utils/api.js'

const { t } = useI18n()

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  // Azione da modificare, o null per crearne una nuova
  action: { type: Object, default: null },
  isNew: { type: Boolean, default: false },
  // Id già usati nella biblioteca: servono a rifiutare un duplicato prima del salvataggio
  existingIds: { type: Array, default: () => [] },
  skills: { type: Array, default: () => [] },
  // Campi di tutti i tipi di processo: un'azione non appartiene a un tipo solo
  fields: { type: Array, default: () => [] },
  // Profili operativi dichiarati in cfg.json, per il selettore di chi può lanciare l'azione
  roles: { type: Array, default: () => [] },
  // Etichette degli stati che richiamano questa azione, per l'avviso in fondo
  usedBy: { type: Array, default: () => [] },
  // Catalogo dei tool (GET configs/tools → catalog): [{ id, label, calls: [{ id, label }] }]
  tools: { type: Array, default: () => [] },
  // Configurazione corrente dei tool (tools.json): { <tool>: { calls: { <call>: {...} } } }.
  // Serve solo a mostrare, come placeholder, cartella e nome che l'azione erediterebbe.
  toolsConfig: { type: Object, default: () => ({}) }
})

const emit = defineEmits(['update:modelValue', 'commit'])

const roleItems = computed(() =>
  props.roles.map(r => ({ title: `${r.label || r.id} (${r.id})`, value: r.id })))

const ID_PATTERN = /^[A-Za-z][A-Za-z0-9_-]*$/
const MAX_RUNS_CEILING = 99

const draft = ref(null)
const errors = ref([])

// Rete di sicurezza: una v-combobox con `return-object` attivo scriverebbe l'intero item nel
// modello, e nel file finirebbe un oggetto al posto del nome
const asName = (value) => (value && typeof value === 'object' ? (value.value ?? value.title ?? '') : (value || ''))

// Cartelle scritte a mano nella combobox: separatori uniformati, slash di bordo e vuoti via.
const cleanFolders = (list) => (Array.isArray(list) ? list : [])
  .map(f => String(f || '').replace(/\\/g, '/').replace(/^\/+|\/+$/g, '').trim())
  .filter(Boolean)

const skillItems = computed(() => props.skills.map(s => ({ title: s.id, value: s.id })))

// Stato della skill: '' (c'è) o 'new' (non esiste ancora: va creata dall'editor delle skill)
const skillState = (action) => {
  if (!action?.skill || !props.skills.length) return ''
  return props.skills.some(s => s.id === asName(action.skill)) ? '' : 'new'
}

// Due soli motori: backend (IA, DeepSeek) e tool (servizio esterno, nessun modello)
const engineItems = computed(() => [
  { title: t('admConfig.statusEditor.verifications.engineBackend'), value: 'backend' },
  { title: t('admConfig.statusEditor.verifications.engineTool'), value: 'tool' }
])

// Motore tool: niente skill, step, allegati o modello — solo tool + chiamata del suo catalogo
const isTool = computed(() => draft.value?.engine === 'tool')
const toolItems = computed(() => props.tools.map(tool => ({ title: `${tool.label} (${tool.id})`, value: tool.id })))
const callItems = computed(() => {
  const tool = props.tools.find(x => x.id === draft.value?.tool)
  return (tool?.calls || []).map(c => ({ title: `${c.label} (${c.id})`, value: c.id }))
})

// Documento prodotto dalla chiamata scelta, come lo dichiara il catalogo: null quando la chiamata
// non genera file (e allora la sezione non si mostra nemmeno).
const callFileSpec = computed(() => {
  const tool = props.tools.find(x => x.id === draft.value?.tool)
  return (tool?.calls || []).find(c => c.id === draft.value?.call)?.file || null
})

// Cosa si eredita non compilando il campo: il pannello del tool, o il default della chiamata.
const toolFilePlaceholder = (key) => {
  const configured = props.toolsConfig?.[draft.value?.tool]?.calls?.[draft.value?.call]?.file?.[key]
  if (configured) return configured
  const spec = callFileSpec.value
  if (!spec) return ''
  if (key === 'folder') return spec.defaultFolder || t('admConfig.tools.fileFolderRoot')
  if (key === 'nameTemplate') return spec.defaultNameTemplate || ''
  return ''
}

// Campi checkbox di tutti i tipi: l'azione può essere richiamata da stati di tipi diversi, quindi
// il campo di validazione non si può filtrare su un tipo solo. I duplicati di nome si fondono.
const checkboxFieldItems = computed(() => {
  const seen = new Map()
  props.fields
    .filter(f => f.type === 'checkbox')
    .forEach(f => {
      if (!seen.has(f.name)) seen.set(f.name, { title: `${f.label || f.name} (${f.name})`, value: f.name })
    })
  return [...seen.values()]
})

// Modelli disponibili: quelli DeepSeek con tariffa configurata nel Backend, più il default.
const modelInfo = ref(null)
const defaultModel = computed(() => modelInfo.value?.defaultModel || '')
const modelItems = computed(() => modelInfo.value?.models || [])

const modelHint = (model) => {
  const scelto = (model || '').trim()
  if (!scelto) {
    return defaultModel.value
      ? t('admConfig.statusEditor.verifications.modelHintDefault', { model: defaultModel.value })
      : t('admConfig.statusEditor.verifications.modelHint')
  }
  if (modelItems.value.length && !modelItems.value.some(m => m.toLowerCase() === scelto.toLowerCase()))
    return t('admConfig.statusEditor.verifications.modelHintUnpriced')
  return t('admConfig.statusEditor.verifications.modelHintOk')
}

const loadModelInfo = async () => {
  try {
    modelInfo.value = await apiCall('/api/projects/configs/backend-setup', { method: 'GET' })
  } catch {
    modelInfo.value = null // lista non disponibile: la combobox resta libera
  }
}

const addStep = () => {
  if (!Array.isArray(draft.value.steps)) draft.value.steps = []
  draft.value.steps.push({ id: '', label: '', skill: '', validationField: '', maxRuns: 0, folders: [], model: '' })
}

const removeStep = (stepIndex) => {
  draft.value.steps.splice(stepIndex, 1)
}

// L'ordine dell'array è l'ordine di esecuzione: spostare una riga cambia la sequenza.
const moveStep = (stepIndex, delta) => {
  const target = stepIndex + delta
  if (target < 0 || target >= draft.value.steps.length) return
  const [moved] = draft.value.steps.splice(stepIndex, 1)
  draft.value.steps.splice(target, 0, moved)
}

const loadDraft = () => {
  errors.value = []
  const source = props.action || {}
  draft.value = {
    id: source.id || '',
    label: source.label || '',
    icon: source.icon || 'mdi-robot-outline',
    skill: source.skill || '',
    analyzeFiles: !!source.analyzeFiles,
    validationField: source.validationField || '',
    // 0 = nessun limite di esecuzioni (proprietà assente nel file)
    maxRuns: Number(source.maxRuns) > 0 ? Number(source.maxRuns) : 0,
    // Cartelle da montare: vuoto = tutti i file della cartella processo + Documentos
    folders: Array.isArray(source.folders) ? source.folders.map(String) : [],
    // Valori storici ("messages", "managed" o assente) = motore backend
    engine: source.engine === 'tool' ? 'tool' : 'backend',
    tool: source.tool || '',
    call: source.call || '',
    // Nome e cartella del documento per questa azione: vuoti = quelli del pannello del tool
    file: {
      folder: source.file?.folder || '',
      nameTemplate: source.file?.nameTemplate || '',
      footer: source.file?.footer || ''
    },
    model: source.model || '',
    // Profili che possono premere il pulsante: vuoto = chiunque operi la fase
    roles: Array.isArray(source.roles) ? [...source.roles] : [],
    enabled: source.enabled !== false,
    // Condizioni di attivazione: nel file può esserci l'oggetto singolo o la lista, qui è sempre lista
    conditionalOn: Array.isArray(source.conditionalOn)
      ? source.conditionalOn.map(c => ({ ...c }))
      : (source.conditionalOn ? [{ ...source.conditionalOn }] : []),
    steps: Array.isArray(source.steps)
      ? source.steps.map(s => ({
          id: s.id || '',
          label: s.label || '',
          skill: s.skill || '',
          validationField: s.validationField || '',
          maxRuns: Number(s.maxRuns) > 0 ? Number(s.maxRuns) : 0,
          folders: Array.isArray(s.folders) ? s.folders.map(String) : [],
          model: s.model || '',
          ...(s.analyzeFiles !== undefined ? { analyzeFiles: !!s.analyzeFiles } : {})
        }))
      : []
  }
}

// Solo le proprietà davvero compilate finiscono nel file: una sezione tutta vuota non si scrive,
// così l'azione continua a seguire il pannello del tool.
const cleanFileOverride = (file) => {
  const entry = {}
  for (const key of ['folder', 'nameTemplate', 'footer']) {
    const value = (file?.[key] || '').trim()
    if (value) entry[key] = value
  }
  return Object.keys(entry).length ? entry : null
}

// Ricompone l'azione nella forma che finisce in cfg.json: le proprietà vuote non si scrivono.
const commit = () => {
  const list = []
  const d = draft.value
  const actionId = (d.id || '').trim()

  if (!actionId || !ID_PATTERN.test(actionId)) {
    list.push(t('admConfig.aiActions.errId'))
  } else if (props.isNew && props.existingIds.includes(actionId)) {
    list.push(t('admConfig.aiActions.errIdDuplicate', { id: actionId }))
  }
  if (!d.label) list.push(t('admConfig.statusEditor.verifications.errLabel', { id: actionId }))

  const cleanSteps = []
  const seenStepIds = new Set()
  // Azione su tool: gli step non esistono, qualunque residuo nel draft non finisce nel file
  ;(isTool.value ? [] : (d.steps || [])).filter(s => s.id || s.label || s.skill || s.validationField).forEach(s => {
    const stepId = (s.id || '').trim()
    if (!stepId) list.push(t('admConfig.statusEditor.verifications.errStepId', { id: actionId }))
    if (seenStepIds.has(stepId)) {
      list.push(t('admConfig.statusEditor.verifications.errStepDuplicate', { id: actionId, step: stepId }))
    }
    seenStepIds.add(stepId)
    if (!s.label) list.push(t('admConfig.statusEditor.verifications.errStepLabel', { id: actionId, step: stepId }))
    if (!s.skill) list.push(t('admConfig.statusEditor.verifications.errStepSkill', { id: actionId, step: stepId }))

    const step = { id: stepId, label: s.label, skill: asName(s.skill) }
    if (s.validationField) step.validationField = asName(s.validationField)
    if (Number(s.maxRuns) > 0) step.maxRuns = Number(s.maxRuns)
    const stepFolders = cleanFolders(s.folders)
    if (stepFolders.length) step.folders = stepFolders
    if ((s.model || '').trim()) step.model = s.model.trim()
    if (Number(s.maxRuns) > MAX_RUNS_CEILING) {
      list.push(t('admConfig.statusEditor.verifications.errMaxRuns', { id: actionId, max: MAX_RUNS_CEILING }))
    }
    if (s.analyzeFiles !== undefined) step.analyzeFiles = !!s.analyzeFiles
    cleanSteps.push(step)
  })

  // Procedura a più step: la skill sta sugli step, quella dell'azione non serve più
  if (!isTool.value && !d.skill && !cleanSteps.length) list.push(t('admConfig.statusEditor.verifications.errSkill', { id: actionId }))
  if (isTool.value && (!d.tool || !d.call)) list.push(t('admConfig.aiActions.errTool', { id: actionId }))
  if (Number(d.maxRuns) > MAX_RUNS_CEILING) {
    list.push(t('admConfig.statusEditor.verifications.errMaxRuns', { id: actionId, max: MAX_RUNS_CEILING }))
  }

  if (list.length) {
    errors.value = list
    return
  }

  // Un profilo eliminato dalla biblioteca resterebbe orfano nel file e il Backend rifiuterebbe
  // il salvataggio: meglio dirlo qui, con il nome sotto gli occhi.
  const knownRoles = new Set(props.roles.map(r => r.id))
  const cleanRoles = [...new Set((d.roles || []).filter(Boolean))]
  cleanRoles.filter(r => !knownRoles.has(r)).forEach(r => {
    list.push(t('admConfig.statusEditor.status.errUnknownRole', { role: r }))
  })

  if (list.length) {
    errors.value = list
    return
  }

  const entry = { id: actionId, label: d.label }
  if (isTool.value) {
    // Solo tool + chiamata: input, output e regola di esito stanno in tools.json
    entry.engine = 'tool'
    entry.tool = asName(d.tool)
    entry.call = asName(d.call)
    if (d.icon) entry.icon = d.icon
    if (Number(d.maxRuns) > 0) entry.maxRuns = Number(d.maxRuns)
    if (d.enabled === false) entry.enabled = false
    if (cleanRoles.length) entry.roles = cleanRoles
    if (d.conditionalOn?.length) entry.conditionalOn = d.conditionalOn
    const file = cleanFileOverride(d.file)
    if (file) entry.file = file
    emit('commit', entry)
    emit('update:modelValue', false)
    return
  }
  if (d.skill) entry.skill = asName(d.skill)
  if (cleanSteps.length) entry.steps = cleanSteps
  if (d.icon) entry.icon = d.icon
  if (d.analyzeFiles) entry.analyzeFiles = true
  if (d.validationField) entry.validationField = asName(d.validationField)
  // Il limite finisce nel file solo se davvero configurato: cfg.json resta pulito dove non serve
  if (Number(d.maxRuns) > 0) entry.maxRuns = Number(d.maxRuns)
  const folders = cleanFolders(d.folders)
  if (folders.length) entry.folders = folders
  if ((d.model || '').trim()) entry.model = d.model.trim()
  // Il motore si scrive sempre esplicito, anche se l'assenza vale già backend
  entry.engine = 'backend'
  // Solo lo spegnimento finisce nel file: l'assenza della proprietà significa accesa
  if (d.enabled === false) entry.enabled = false
  // Nessun profilo = proprietà assente: l'azione resta aperta a chiunque operi la fase
  if (cleanRoles.length) entry.roles = cleanRoles
  // Nessuna condizione = proprietà assente: il pulsante è sempre attivo, com'era prima
  if (d.conditionalOn?.length) entry.conditionalOn = d.conditionalOn

  emit('commit', entry)
  emit('update:modelValue', false)
}

const close = (value) => {
  if (value === true) return
  emit('update:modelValue', false)
}

watch(() => props.modelValue, (open) => {
  if (!open) return
  loadDraft()
  if (!modelInfo.value) loadModelInfo()
})
</script>

<style scoped>
.step-row {
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  padding-top: 6px;
}
</style>
